import React, { Component } from 'react'
import PropTypes from 'prop-types'
import * as Sentry from '@sentry/react'
import { connect } from 'react-redux'
import { injectIntl, FormattedMessage } from 'react-intl'
import { TextInput, Button } from '@knowledgehound/laika'
import { actions, selectors } from '@knowledgehound/stories'
import * as cards from '@knowledgehound/cards'
import _uniqueId from 'lodash/uniqueId'
import _capitalize from 'lodash/capitalize'

import ModalControls from './ModalControls'
import * as modalStyles from './Modal.module.scss'
import * as styles from './AddToStory.module.scss'

class AddToStory extends Component {
  static propTypes = {
    content_object: PropTypes.object.isRequired,
    content_type: PropTypes.oneOf([
      'linkedstudy',
      'linkeddocument',
      'linkedanalysis',
      'linkedopenendedquote',
      'linkedvideo',
      'linkedvpmvideo',
    ]).isRequired,
    id: PropTypes.string,
    name: PropTypes.string,
    study: PropTypes.object,
    currentStories: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    })),
    author: PropTypes.string,
    intl: PropTypes.object.isRequired,
    newCard: PropTypes.object.isRequired,
    newStory: PropTypes.object.isRequired,
    onCreateError: PropTypes.func.isRequired,
    onClickViewStory: PropTypes.func,
    handleClose: PropTypes.func.isRequired,
    successMessage: PropTypes.func.isRequired,
  }

  state = {
    title: '',
    description: '',
    selectedStory: undefined,
    newStorySelected: false,
    newStoryTitle: '',
  }

  componentDidMount () {
    this.fetchAllStories()
  }

  fetchAllStories() {
    const { dispatch, author } = this.props
    dispatch(actions.fetchStories({ author }))
  }

  handleChange = (evt) => {
    this.setState({
      [evt.currentTarget.id]: evt.currentTarget.value
    })
  }

  handleChangeDropdown = (evt) => {
    this.setState({
      selectedStory: evt.target.selectedOptions[0].value,
      newStorySelected: false,
      newStoryTitle: '',
    })
  }

  getType (contentType) {
    const typeMap = {
      linkedstudy: 'study',
      linkeddocument: 'document',
      linkedanalysis: 'question', // Parity with A1
      linkedopenendedquote: 'open_ended_question',
      linkedvideo: 'video',
      linkedvpmvideo: 'vpm_video',

    }
    return typeMap[contentType]
  }

  getPropertyNamesByType (contentType) {
    const typeMap = {
      study: {
        id: 'study_id',
        name: 'study_name'
      },
      document: {
        id: 'document_id',
        name: 'document_name'
      },
      question: {
        id: 'question_id',
        name: 'question_text'
      },
      open_ended_question: {
        id: 'question_id',
        name: 'question_text'
      },
      video: {
        id: 'video_id',
        name: null
      },
      vpm_video: {
        id: 'vpm_video_id',
        name: null
      },
    }
    return typeMap[contentType]
  }

  handleClose = () => {
    this.setState({
      selectedStory: undefined,
      newStorySelected: false,
      newStoryTitle: ''
    })
    this.props.handleClose()
  }

  createCard = (story, card) => {
    const {
      dispatch,
      content_type,
      name,
      id,
      study,
      onClickViewStory,
      onCreateError
    } = this.props

    if (story) {
      this.handleClose()
      dispatch(cards.actions.createCard(story.id, card))
        .then(() => {
          if (story) {
            this.props.successMessage(
              `Card was added to ${story && story.title}`,
              {
                action: 'View',
                onClick: () => {
                  if (story && story.id) {
                    if (onClickViewStory) {
                      return onClickViewStory(story.id)
                    } else {
                      window.location.assign(`/stories/${story.id}`)
                    }
                  }
                }
              }
            )
            const type = this.getType(content_type)
            const propertyNames = this.getPropertyNamesByType(type)
            if (propertyNames) {
              const itemId = propertyNames.id ? propertyNames.id : 'id'
              const itemName = propertyNames.name ? propertyNames.name : 'name'
              let trackPayload = {
                [itemId]: id,
                [itemName]: name,
                story_id: story.id,
                story_title: story.title
              }
              if (study && type !== 'study') {
                trackPayload = {
                  ...trackPayload,
                  study_name: study && study.study_name,
                  study_number: study && study.study_number
                }
              }
              const typeName = type ? _capitalize(type) : 'Object'
              window.analytics.track(
                `${typeName} Added to Story`,
                trackPayload
              )
            }
          }
        })
        .catch(err => {
          this.context.sendNotification(
            _uniqueId('_addToStoryModal'),
            'error',
            `There was an error adding the card to the story, please try again.  If the error persists please contact customer support.`,
            {
              action: 'Try Again',
              onClick: onCreateError,
            }
          )
          Sentry.captureException(new Error('Error occured while creating a card', { cause: err }))
        })
    }
  }

  toggleView = (evt) => {
    this.setState({
      newStorySelected: !this.state.newStorySelected
    })
  }

  handleChangeNewStory = (evt) => {
    this.setState({
      newStorySelected: true,
      selectedStory: undefined,
    })
  }

  handleSubmit = (evt) => {
    evt.preventDefault()
    const {
      dispatch,
      currentStories,
      author,
      content_type,
      content_object
    } = this.props
    const { selectedStory, newStoryTitle, description, title } = this.state
    const fullStory = currentStories.find(story => story.id === selectedStory)

    function buildCard () {
      return {
        title,
        description,
        content_type,
        content_object,
      }
    }

    if (newStoryTitle && author) {
      dispatch(
        actions.createStory({
          title: newStoryTitle,
          author,
          is_public: false
        })
      )
        .then(newStory => {
          this.createCard(newStory, buildCard())
          this.fetchAllStories()
          window.analytics.track('Story Created', {
            story_id: newStory.id,
            story_title: newStory.title
          })
        })
        .catch(err => {
          Sentry.captureException(new Error('Error occured while creating a story', { cause: err }))
        })
    } else {
      if (fullStory) {
        this.createCard(fullStory, buildCard())
      }
    }
  }

  render() {
    const {
      currentStories,
      intl,
      newCard,
      newStory,
      isFetchingStories,
    } = this.props

    const {
      newStoryTitle,
      newStorySelected,
      selectedStory,
    } = this.state

    const showExisting = currentStories.length > 0 && !newStorySelected
    const existingOrNewToggle = showExisting ?  'Create a new story' : 'Select an existing story'

    return (
      <section>
        {newStory.error && (
          <p className={modalStyles.errorMessage}>
            <FormattedMessage id='addToStory.newStoryError' />
          </p>
        )}
        {newCard.error && (
          <p className={modalStyles.errorMessage}>
            <FormattedMessage id='addToStory.newCardError' />
          </p>
        )}
        <form
          className={modalStyles.addForm}
          onSubmit={this.handleSubmit}
          name='add-to-story'
          action='.'
        >
          <div>
            {showExisting ? (
              <select
                disabled={isFetchingStories}
                className={styles.storyDropdown}
                value={selectedStory}
                defaultValue=""
                onChange={this.handleChangeDropdown}
              >
                <option value="" disabled>
                  {intl.formatMessage({ id: 'addToStory.selectExistingStory' })}
                </option>
                {currentStories.map(story => (
                  <option key={story.id} value={story.id}>
                    {story.title}
                  </option>
                ))}
              </select>
            ) : (
              <TextInput
                name='newStoryTitle'
                id='newStoryTitle'
                maxLength={40}
                disabled={isFetchingStories}
                placeholder={intl.formatMessage({
                  id: `addToStory.${isFetchingStories ? 'loading' : 'newStoryTitle'}`
                })}
                value={newStoryTitle}
                onChange={this.handleChange}
              />
            )}
            {currentStories.length > 0 && (
              <div className={styles.toggleContainer}>
                <Button variant="text" size="small" onClick={this.toggleView}>
                  {existingOrNewToggle}
                </Button>
              </div>
            )}
          </div>
          <section>
            <label
              className={modalStyles.label}
              htmlFor='description'
            >
              <FormattedMessage id='addToStory.descriptionLabel' />
            </label>
            <textarea
              className={modalStyles.descriptionField}
              name='description'
              id='description'
              placeholder={intl.formatMessage({
                id: 'addToStory.descriptionPlaceholder'
              })}
              onChange={this.handleChange}
              maxLength={200}
            />
          </section>
          <ModalControls
            onCancel={this.handleClose}
            submitDisabled={isFetchingStories || (!selectedStory && !(newStoryTitle.length > 0))}
          />
        </form>
      </section>
    )
  }
}

const mapStateToProps = state => ({
  currentStories: selectors.getStoriesList(state),
  isFetchingStories: selectors.getIsFetchingStories(state),
})

export default injectIntl(connect(mapStateToProps)(AddToStory))
